/**
 * The authority behind the board.
 *
 * NWGA Connect is a service of the Northwest Georgia Joint Development
 * Authority, not a product of its own, and the chrome says so: the header links
 * back to the main site, the footer carries the same address, phone and social
 * accounts that northwestgeorgia.us does. Those details live here rather than
 * in the footer component because they are shared vocabulary — the same phone
 * number belongs in a transactional email signature, and one wrong copy of it
 * is worse than none.
 *
 * Keep in sync with `src/content/settings/site.yaml` in the main site, which is
 * the editorial source of truth for the same facts.
 */

export const ORGANIZATION = {
	name: "Northwest Georgia Joint Development Authority",
	shortName: "Northwest Georgia JDA",
	abbr: "NWGA JDA",
	tagline: "Business at the top of Georgia",
	/** The main site this one extends. */
	url: "https://northwestgeorgia.us",
	contact: {
		address: "10052 N. Hwy. 27",
		city: "Rock Spring",
		state: "GA",
		zip: "30739",
		phone: "706.375.5793",
		fax: "706.375.5795",
		email: "info@northwestgeorgia.us",
		mapQuery: "10052 N. Hwy. 27, Rock Spring, GA 30739",
	},
	social: {
		linkedin:
			"https://www.linkedin.com/company/northwest-ga-joint-development-authority/",
		facebook: "https://www.facebook.com/topofgeorgia",
		instagram: "https://www.instagram.com/topofgeorgia",
	},
} as const;

/**
 * The product this site is: a named service of the authority above.
 *
 * NWGA Connect is the sub-site northwestgeorgia.us's "Jobs" tab points at, and
 * the header carries a labelled way back for the people who arrive that way.
 * It does have a home page of its own — most visitors reach a listing from a
 * search engine, not from the main site's nav, and they need somewhere on this
 * origin that says what the platform is. That page introduces *the platform*;
 * anything introducing *the Authority* belongs on the main site.
 */
export const PRODUCT = {
	name: "NWGA Connect",
	tagline: "Work close to home",
	/**
	 * The path on the *main* site that links here. Keep in sync with its nav; it
	 * is what the back-link and the top breadcrumb return to.
	 */
	mainSitePath: "/",
} as const;

/** How the main site is named in links back to it. */
export const MAIN_SITE_LABEL = "northwestgeorgia.us";

/** `tel:` hrefs cannot contain the dots the phone number is written with. */
export const phoneHref = `tel:${ORGANIZATION.contact.phone.replace(/\./g, "")}`;

export const emailHref = `mailto:${ORGANIZATION.contact.email}`;

export const mapHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
	ORGANIZATION.contact.mapQuery,
)}`;

/** Pages on the main site the footer and header point back to. */
export const MAIN_SITE_LINKS = [
	{ href: "/about", label: "About the Authority" },
	{ href: "/business-advantages/workforce", label: "Workforce" },
	{ href: "/communities", label: "Our communities" },
	{ href: "/site-selection", label: "Site selection" },
	{ href: "/news", label: "News & events" },
] as const;

/**
 * Absolute URL for a path on the main site.
 *
 * Concatenated rather than built with `URL`, which this package cannot reach
 * for: contracts compiles without DOM or Node lib types precisely so it stays
 * usable from every runtime above it.
 */
export function mainSiteUrl(path: string): string {
	return `${ORGANIZATION.url}${path.startsWith("/") ? path : `/${path}`}`;
}
